import { useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import SEOHead, { getWebPageSchema, getBreadcrumbSchema } from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics, categories, siteInfo } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');
  const activeSlug = slug || 'all';

  const activeCategory = categories.find((c) => c.slug === activeSlug);
  const categoryName = activeCategory?.name || '全部';

  const filteredComics = useMemo(() => {
    let result = activeSlug === 'all' ? [...comics] : comics.filter((c) => c.categories.includes(activeSlug));

    switch (sortBy) {
      case 'latest':
        result.sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'popular':
      default:
        result.sort((a, b) => b.viewCount - a.viewCount);
        break;
    }
    return result;
  }, [activeSlug, sortBy]);

  const handleCategoryChange = (newSlug: string) => {
    navigate(`/category/${newSlug}`);
  };

  const sortOptions: { value: typeof sortBy; label: string }[] = [
    { value: 'popular', label: '最热' },
    { value: 'latest', label: '最新' },
    { value: 'rating', label: '评分' },
  ];

  return (
    <>
      <SEOHead
        title={`${categoryName}漫画 - ${siteInfo.name}`}
        description={`吹雪漫画网${categoryName}漫画分类，海量高品质${categoryName}漫画免费阅读，无删减完整版，下拉式流畅阅读体验。`}
        keywords={`${categoryName}漫画,免费阅读,吹雪漫画网,无删减漫画`}
        canonicalUrl={`https://chuixue.manhua.com/category/${activeSlug}`}
        schemaData={{
          ...getWebPageSchema(
            `${categoryName}漫画 - ${siteInfo.name}`,
            `吹雪漫画网${categoryName}漫画分类，海量高品质${categoryName}漫画免费阅读。`,
            `https://chuixue.manhua.com/category/${activeSlug}`,
          ),
          ...getBreadcrumbSchema([
            { name: '首页', url: 'https://chuixue.manhua.com/' },
            { name: `${categoryName}漫画`, url: `https://chuixue.manhua.com/category/${activeSlug}` },
          ]),
        }}
      />

      <Breadcrumb
        items={[
          { name: '首页', href: '/' },
          { name: '漫画分类', href: '/category/all' },
          { name: categoryName },
        ]}
      />

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-6">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
            {categoryName}漫画
          </h1>
          <p className="text-foreground-500 text-sm">
            发现精彩{categoryName}漫画，海量高品质作品免费阅读
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          <div className="sm:ml-auto flex items-center gap-1 bg-background-100 rounded-full p-1">
            {sortOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSortBy(opt.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  sortBy === opt.value
                    ? 'bg-background-50 text-foreground-900 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
          {filteredComics.map((comic) => (
            <Link
              key={comic.id}
              to={`/comic/${comic.id}`}
              className="group cursor-pointer"
            >
              <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-2 left-2">
                  <span className="px-2 py-0.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                    {comic.rating}
                  </span>
                </div>
                {comic.status === '连载中' && (
                  <div className="absolute top-2 right-2">
                    <span className="px-2 py-0.5 rounded-full bg-accent-500 text-background-50 text-xs">
                      连载
                    </span>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                  <p className="text-white text-xs">
                    更新至 {comic.chapters.length} 话
                  </p>
                </div>
              </div>
              <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
                {comic.title}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-xs text-foreground-400">{comic.author}</p>
                <span className="text-xs text-foreground-300">·</span>
                <p className="text-xs text-foreground-400">
                  {comic.viewCount > 10000
                    ? `${(comic.viewCount / 10000).toFixed(0)}万阅读`
                    : `${comic.viewCount}阅读`}
                </p>
              </div>
            </Link>
          ))}
        </div>

        {filteredComics.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-inbox-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 text-sm">该分类暂无漫画，请浏览其他分类</p>
          </div>
        )}
      </div>
    </>
  );
}